/**
 * Auth state derived from `GET /v1/me`.
 *
 * - Cached forever via TanStack Query; invalidate on logout.
 * - 401 → user is treated as logged out, not as an error.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { auth } from "@/lib/endpoints";
import type { UserResponse } from "@/lib/api-types";

const ME_KEY = ["auth", "me"] as const;

export function useAuth() {
  const query = useQuery<UserResponse | null>({
    queryKey: ME_KEY,
    queryFn: async () => {
      try {
        return await auth.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isAuthenticated: !!query.data,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => auth.logout(),
    onSuccess: () => {
      qc.setQueryData(ME_KEY, null);
      qc.invalidateQueries();
    },
  });
}
